export type FeatureLike = number | boolean | null | string

const textEncoder = new TextEncoder()

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length)
  out.set(a, 0)
  out.set(b, a.length)
  return out
}

function bytesToBase64(bytes: Uint8Array): string {
  // Avoid call stack limits and Uint8Array iteration requirements by chunking
  // and manually converting bytes -> binary string.
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    let chunkStr = ''
    for (let j = 0; j < chunk.length; j++) {
      chunkStr += String.fromCharCode(chunk[j])
    }
    binary += chunkStr
  }
  return btoa(binary)
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

function encodeFeatureListLittleEndian(features: number[]): Uint8Array {
  const buf = new ArrayBuffer(4 + features.length * 8)
  const view = new DataView(buf)
  view.setUint32(0, features.length, true)
  for (let i = 0; i < features.length; i++) {
    view.setFloat64(4 + i * 8, features[i], true)
  }
  return new Uint8Array(buf)
}

function decodeFeatureListLittleEndian(bytes: Uint8Array): number[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.byteLength < 4) {
    throw new Error('Invalid feature payload: too short')
  }
  const n = view.getUint32(0, true)
  const expected = 4 + n * 8
  if (view.byteLength !== expected) {
    throw new Error(
      `Invalid feature payload: length mismatch (expected ${expected}, got ${view.byteLength})`
    )
  }
  const out: number[] = new Array(n)
  for (let i = 0; i < n; i++) {
    out[i] = view.getFloat64(4 + i * 8, true)
  }
  return out
}

async function deriveAes256KeyFromString(keyStr: string): Promise<CryptoKey> {
  const keyMaterial = textEncoder.encode(keyStr)
  const digest = await crypto.subtle.digest('SHA-256', keyMaterial)
  return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ])
}

export function featureLikeToFloat64(value: FeatureLike, index?: number): number {
  if (value === null) return Number.NaN
  if (typeof value === 'number') return value
  if (typeof value === 'boolean') return value ? 1.0 : 0.0
  if (typeof value === 'string') {
    const trimmed = value.trim()
    const f = Number.parseFloat(trimmed)
    if (Number.isNaN(f)) {
      throw new Error(
        `Invalid numeric feature${index === undefined ? '' : ` at index ${index}`}: ${JSON.stringify(value)}`
      )
    }
    return f
  }
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  throw new Error(`Invalid feature type${index === undefined ? '' : ` at index ${index}`}: ${typeof value}`)
}

/**
 * Encrypts the float64 feature list using the same logic as liberal_runner:
 * - key = sha256(keyStr)
 * - plaintext = little-endian uint32(len) + float64[] (little-endian)
 * - AES-256-GCM with random nonce
 * - output = nonce || ciphertext
 * - JSON transports it as base64
 */
export async function encryptFeatureListToBase64(
  keyStr: string,
  features: number[]
): Promise<string> {
  const key = await deriveAes256KeyFromString(keyStr)
  const plain = encodeFeatureListLittleEndian(features)
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    plain
  )
  const out = concatBytes(nonce, new Uint8Array(ciphertext))
  return bytesToBase64(out)
}

export async function decryptFeatureListFromBase64(
  keyStr: string,
  encryptedBase64: string
): Promise<number[]> {
  const key = await deriveAes256KeyFromString(keyStr)
  const all = base64ToBytes(encryptedBase64.trim())
  if (all.length < 12 + 16) {
    throw new Error('Invalid encrypted payload: too short')
  }
  const nonce = all.subarray(0, 12)
  const ciphertext = all.subarray(12)
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    ciphertext
  )
  return decodeFeatureListLittleEndian(new Uint8Array(plainBuf))
}

